// The arenas battles happen in: one small clearing per kind of place, looking like where you were (a meadow,
// the deep woods, the graveyard, the summit). Each fight gets its own copy (an instance). The party starts on
// the right, the creatures on the left, and a glowing gate on the right edge is the way out.
import { T, ZoneBuilder, rng } from './zonebuild.js';
import type { ZoneDef } from './zones.js';

export const ARENA_W = 26, ARENA_H = 15;
/** The gate out (tile), and where each side stands at the start (tiles). */
export const ARENA_EXIT: [number, number] = [ARENA_W - 2, 7];
export const PARTY_AT: [number, number] = [18, 7], FOES_AT: [number, number] = [7, 7];
/** A big battle (a whole alliance against a world boss) gets a bigger field: the camera eases out to show it all. */
export const BIG_W = 40, BIG_H = 23;
export interface ArenaGeo { exit: [number, number]; party: [number, number]; foes: [number, number]; w: number; h: number }
const SMALL_GEO: ArenaGeo = { exit: ARENA_EXIT, party: PARTY_AT, foes: FOES_AT, w: ARENA_W, h: ARENA_H };
const BIG_GEO: ArenaGeo = { exit: [BIG_W - 2, 11], party: [28, 11], foes: [11, 11], w: BIG_W, h: BIG_H };
/** Where the gate and each side are in an arena (by its map id). */
export const arenaGeo = (id: string): ArenaGeo => id.endsWith('-big') ? BIG_GEO : SMALL_GEO;
export type ArenaPlace = 'meadow' | 'woods' | 'grave' | 'spine' | 'crags' | 'deck';
export const ARENA_PLACES: ArenaPlace[] = ['meadow', 'woods', 'grave', 'spine', 'crags', 'deck'];

/** A fight on the ferry's deck: planks from rail to rail, the sea all round, crates to dodge behind. */
function deckArena(big: boolean): ZoneDef {
  const G = big ? BIG_GEO : SMALL_GEO, W = G.w, H = G.h, b = new ZoneBuilder(W, H, T.WATER);
  for (let y = 2; y < H - 2; y++) for (let x = 1; x < W - 1; x++) b.fill(x, y, x, y, T.PIER);
  for (let x = 0; x < W; x++) { b.block(x, 0); b.block(x, 1); b.block(x, H - 1); b.block(x, H - 2); }
  for (let y = 0; y < H; y++) { b.block(0, y); if (!(Math.abs(y - G.exit[1]) <= 1)) b.block(W - 1, y); }
  b.fenceH(1, W - 2, 1).fenceH(1, W - 2, H - 2);
  const cover: [number, number][] = big ? [[18, 5], [19, 17], [8, 9], [30, 14]] : [[12, 4], [13, 10], [5, 7], [20, 5]];
  for (const [x, y] of cover) b.put(x % 2 ? 'crate' : 'barrel', x, y);
  const f = b.finish(), id = `arena-deck${big ? '-big' : ''}`;
  return { id, name: 'On deck', kind: 'arena', mounts: false, flying: false, map: { id, name: 'Battle', width: W, height: H, tileSize: 16, spawn: G.party, collision: f.collision }, art: f.art, wayshrine: null, mobs: [] };
}
function arena(place: ArenaPlace, big = false): ZoneDef {
  if (place === 'deck') return deckArena(big);
  const G = big ? BIG_GEO : SMALL_GEO, ARENA_EXIT = G.exit, W = G.w, H = G.h, rocky = place === 'spine' || place === 'crags', base = place === 'woods' ? T.FOREST : rocky ? T.ROCK : T.GRASS;
  const b = new ZoneBuilder(W, H, base), r = rng(place.length * 977 + 31 + (big ? 5 : 0));
  // an open middle (a softer patch of ground), and a ring of whatever grows here
  b.ellipse(W / 2, H / 2, W / 2 - 2.5, H / 2 - 2, rocky ? T.SAND : T.GRASS);
  for (let x = 0; x < W; x++) for (let y = 0; y < H; y++) {
    const edge = x === 0 || y === 0 || x === W - 1 || y === H - 1, nearEdge = x <= 1 || y <= 1 || x >= W - 2 || y >= H - 2;
    if (x === ARENA_EXIT[0] + 1 && Math.abs(y - ARENA_EXIT[1]) <= 1) continue;   // the gate's gap
    if (edge) { b.block(x, y); continue; }
    if (!nearEdge || (x === ARENA_EXIT[0] && Math.abs(y - ARENA_EXIT[1]) <= 2)) continue;
    if (r() < 0.7) {
      if (place === 'grave') b.put(r() < 0.5 ? 'tomb' : 'deadtree', x, y, Math.floor(r() * 3));
      else if (place === 'spine') b.put(r() < 0.6 ? 'rock' : 'peak', x, y, Math.floor(r() * 3));
      else if (place === 'crags') b.put(r() < 0.45 ? 'rock' : r() < 0.6 ? 'pine' : 'peak', x, y, Math.floor(r() * 3));
      else b.tree(x, y, place === 'woods' ? (r() < 0.6 ? 'pine' : 'oak') : (r() < 0.25 ? 'blossom' : r() < 0.5 ? 'pine' : 'oak'));
    } else b.block(x, y);
  }
  // the gate: a little path out on the right
  b.fill(ARENA_EXIT[0] - 1, ARENA_EXIT[1], W - 1, ARENA_EXIT[1], T.PATH);
  // a few things to stand behind, out of the way of the start positions
  const cover: [number, number][] = big ? [[18, 4], [19, 18], [6, 17], [32, 4], [6, 5], [33, 18]] : [[12, 3], [13, 11], [4, 11], [21, 3]];
  for (const [x, y] of cover) {
    if (place === 'grave') b.put('tomb', x, y, (x + y) % 3); else if (rocky) b.put('rock', x, y, x % 3); else b.put(place === 'woods' ? 'log' : 'rock', x, y, x % 3);
  }
  b.scatter(7 + place.length, big ? 60 : 26, place === 'grave' ? ['tuft', 'candle'] : rocky ? ['tuft'] : ['tuft', 'tuft', 'mushroom'], [2, 2, W - 3, H - 3], []);
  const f = b.finish(), id = `arena-${place}${big ? '-big' : ''}`;
  return {
    id, name: { meadow: 'A clearing', woods: 'Deep in the woods', grave: 'Among the graves', spine: 'The summit', crags: 'A windy ledge' }[place], kind: 'arena', mounts: false, flying: false,
    map: { id, name: 'Battle', width: W, height: H, tileSize: 16, spawn: G.party, collision: f.collision },
    art: { ...f.art, mood: place === 'grave' ? 'gloom' : place === 'spine' ? 'ember' : undefined }, wayshrine: null, mobs: [],
  };
}

export const ARENAS: ZoneDef[] = [...ARENA_PLACES.map(p => arena(p)), ...ARENA_PLACES.map(p => arena(p, true))];
